import React, { useMemo, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  alpha,
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  CssBaseline,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import loginBackdrop from './assets/login-backdrop.jpg';

import ManageSpecialTourType from './components/ManageTourTypeSpecial';
import AddSpecialTourType from './components/AddTourTypeSpecial';
import EditSpecialTourType from './components/EditTourTypeSpecial';
import ManageDestination from './components/ManageDestination';
import AddDestination from './components/AddDestination';
import EditDestination from './components/EditDestination';
import ManageItinerary from './components/ManageItinerary';
import AddItinerary from './components/AddItinerary';
import EditItinerary from './components/EditItinerary';
import ManageGallery from './components/ManageGallery';
import AddGallery from './components/AddGallery';
import EditGallery from './components/EditGallery';
import Sidebar from './components/Sidebar';
import ManageDiscovery from './components/ManageDiscovery';
import AddEditDiscovery from './components/AddEditDiscovery';
import ManagePartners from './components/ManagePartner';
import AddEditPartner from './components/AddEditPartner';
import ManageLocations from './components/ManageLocation';
import AddEditLocation from './components/AddEditLocation';
import ManageCoreCompetency from './components/ManageCoreCompetency';
import AddEditCoreCompetency from './components/AddEditCoreCompetency';
import ManageContinent from './components/ManageContinent';
import AddEditContinent from './components/AddEditContinent';
import ManageTourType from './components/ManageTourType';
import AddEditTourType from './components/AddEditTourType';
import ManageSpecialFeatures from './components/SpecialFeatureList';
import AddEditSpecialFeature from './components/AddEditSpecialFeature';
import TestimonialList from './components/TestimonialList';
import AddEditTestimonial from './components/AddEditTestimonial';
import HomeMantleList from './components/HomeMantleList';
import AddEditMantle from './components/AddEditMantle';
import AboutMantleList from './components/AboutMantleList';
import AddEditAboutMantle from './components/AddEditAboutMantle';
import ContactList from './components/ContactList';
import UserList from './components/userlist';
import ManageAccrediation from './components/ManageAccrediation';
import AddEditAccrediation from './components/AddEditAccrediation';
import TravelList from './components/travel_data_list';
import DynamicTablePage from './components/DynamicTablePage';
import DynamicTablePageDiscovery from './components/DynamicTablePageDiscovery';
import EditOnlyTablePage from './components/EditOnlyTablePage';

const drawerWidth = 292;
const collapsedWidth = 92;

const pageTitles = [
  { match: '/manage-continents', title: 'Tour Management', subtitle: 'Organize continents, tours, and destination hierarchy.' },
  { match: '/manage-tour-type', title: 'Country Management', subtitle: 'Manage country-level entries and move into package operations quickly.' },
  { match: '/manage-destination', title: 'Destinations', subtitle: 'Create and refine destination pages with richer travel content.' },
  { match: '/manage-itinerary', title: 'Itinerary Management', subtitle: 'Shape destination storytelling through cleaner tab content and media.' },
  { match: '/manage-gallery', title: 'Gallery Management', subtitle: 'Keep destination media organized, ordered, and easy to replace.' },
  { match: '/manage-special-features', title: 'Special Features', subtitle: 'Manage supporting feature cards connected to each destination package.' },
  { match: '/add-special-feature', title: 'Special Features', subtitle: 'Create a new supporting feature card for the selected destination.' },
  { match: '/edit-special-feature', title: 'Special Features', subtitle: 'Update a supporting feature card for the selected destination.' },
  { match: '/edit-table', title: 'Homepage Editor', subtitle: 'Fine-tune shared homepage sections with cleaner control.' },
  { match: '/table', title: 'Data Tables', subtitle: 'Review operational records, contact requests, and content entries.' },
  { match: '/discovery', title: 'Discovery Content', subtitle: 'Manage discovery stories and themed homepage content blocks.' },
  { match: '/manage-core-competency', title: 'Core Competency', subtitle: 'Manage the trust signals and strengths highlighted on the site.' },
  { match: '/home-mantle', title: 'Home Banner', subtitle: 'Refresh the main hero area visitors see first.' },
  { match: '/manage-gallery/add', title: 'Gallery Management', subtitle: 'Add new destination media with clearer previews and save feedback.' },
  { match: '/manage-gallery/edit', title: 'Gallery Management', subtitle: 'Update destination media with clearer previews and save feedback.' },
  { match: '/manage-itinerary/add', title: 'Itinerary Management', subtitle: 'Create a new itinerary tab with better structure and save feedback.' },
  { match: '/manage-itinerary/edit', title: 'Itinerary Management', subtitle: 'Update a destination itinerary tab with better structure and save feedback.' },
  { match: '/manage-partners', title: 'Partners', subtitle: 'Keep premium partner content polished and up to date.', surface: false },
  { match: '/add-partner', title: 'Partners', subtitle: 'Create premium partner entries with cleaner content and preview controls.', surface: false },
  { match: '/edit-partner', title: 'Partners', subtitle: 'Update premium partner content with cleaner content and preview controls.', surface: false }
];

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem('isAuthenticated') === 'true'
  );
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:900px)');

  const handleLogin = () => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
    navigate('/');
    setDesktopSidebarOpen(true);
    setMobileSidebarOpen(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.setItem('isAuthenticated', 'false');
    setDesktopSidebarOpen(true);
    setMobileSidebarOpen(false);
    navigate('/login');
  };

  const toggleDesktopSidebar = () => {
    setDesktopSidebarOpen((prev) => !prev);
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen((prev) => !prev);
  };

  const closeMobileSidebar = () => {
    setMobileSidebarOpen(false);
  };

  const currentMeta = useMemo(() => {
    if (location.pathname === '/') {
      return {
        title: 'Dashboard',
        subtitle: 'Aaryan Leisure & Holidays Control Center to control the content.',
        surface: false
      };
    }

    return pageTitles.find((item) => location.pathname.startsWith(item.match)) || {
      title: 'Admin Workspace',
      subtitle: 'Manage travel content, brand sections, and visitor enquiries with confidence.',
      surface: true
    };
  }, [location.pathname]);

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#031525'
        }}
      >
        <CssBaseline />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${loginBackdrop})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            filter: 'saturate(1.08) contrast(1.02) brightness(0.72)',
            transform: 'scale(1.02)',
            opacity: 0.9
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(115deg, rgba(2, 6, 23, 0.84) 0%, rgba(5, 23, 44, 0.58) 38%, rgba(8, 47, 73, 0.38) 100%)'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at top right, rgba(56, 189, 248, 0.16), transparent 34%), radial-gradient(circle at bottom left, rgba(59, 130, 246, 0.16), transparent 30%)'
          }}
        />
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            minHeight: '100vh',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, md: 4 },
            py: { xs: 3, md: 5 }
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 1240,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.08fr) minmax(420px, 460px)' },
              gap: { xs: 3, lg: 5 },
              alignItems: 'center'
            }}
          >
            <Stack
              spacing={3}
              sx={{
                display: { xs: 'none', lg: 'flex' },
                color: '#ffffff',
                pr: 4
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  component="img"
                  src="https://aaryanholidays.com/static/media/logo.c1413be8b4666faa097349a8205e293c.svg"
                  alt="Aaryan Holidays"
                  sx={{
                    width: 84,
                    height: 84,
                    p: 1.5,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.16), rgba(37, 99, 235, 0.2))',
                    boxShadow: '0 24px 60px rgba(14, 165, 233, 0.18)'
                  }}
                />
                <Box>
                  <Typography sx={{ fontSize: 14, letterSpacing: 3.2, textTransform: 'uppercase', color: alpha('#e0f2fe', 0.84) }}>
                    Aaryan Holidays
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.12 }}>
                    Control every travel story from one premium workspace.
                  </Typography>
                </Box>
              </Stack>

              <Typography sx={{ maxWidth: 640, color: alpha('#e2e8f0', 0.86), fontSize: 18, lineHeight: 1.85 }}>
                Review destinations, update homepage sections, manage enquiries, and keep your global travel presentation polished with one secure admin experience.
              </Typography>

              <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                {['Secure admin access', 'Content-first workflow'].map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    sx={{
                      borderRadius: 999,
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      color: '#ffffff',
                      border: '1px solid rgba(255,255,255,0.12)',
                      backdropFilter: 'blur(10px)',
                      fontWeight: 600
                    }}
                  />
                ))}
              </Stack>
            </Stack>

            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: 520, lg: 'none' },
                mx: 'auto'
              }}
            >
              <Stack spacing={2.5} sx={{ mb: 3, color: '#ffffff', display: { xs: 'flex', lg: 'none' } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    component="img"
                    src="https://aaryanholidays.com/static/media/logo.c1413be8b4666faa097349a8205e293c.svg"
                    alt="Aaryan Holidays"
                    sx={{
                      width: 58,
                      height: 58,
                      p: 1.1,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.16), rgba(37, 99, 235, 0.2))'
                    }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: 12, letterSpacing: 2.6, textTransform: 'uppercase', color: alpha('#e0f2fe', 0.84) }}>
                      Aaryan Holidays
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.12 }}>
                      Admin Access
                    </Typography>
                  </Box>
                </Stack>
                <Typography sx={{ color: alpha('#e2e8f0', 0.86), lineHeight: 1.8 }}>
                  Sign in to manage content, tours, and website updates from a polished admin workspace.
                </Typography>
              </Stack>
              <Login onLogin={handleLogin} />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: '#f8fafc',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(148,163,184,0.55) transparent',
        '& *': {
          scrollbarWidth: 'thin'
        },
        '& *::-webkit-scrollbar': {
          width: 8,
          height: 8
        },
        '& *::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '& *::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(148,163,184,0.45)',
          borderRadius: 999,
          border: '2px solid transparent',
          backgroundClip: 'padding-box'
        },
        '& *::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(100,116,139,0.58)'
        }
      }}
    >
      <CssBaseline />

      <Sidebar
        open={desktopSidebarOpen}
        mobileOpen={mobileSidebarOpen}
        isMobile={isMobile}
        onCloseMobile={closeMobileSidebar}
        onToggleDesktop={toggleDesktopSidebar}
        onLogout={handleLogout}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          transition: 'all 220ms ease'
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            top: 0,
            backdropFilter: 'blur(14px)',
            backgroundColor: alpha('#f8fafc', 0.82),
            borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
            color: '#0f172a'
          }}
        >
          <Toolbar sx={{ px: { xs: 2, md: 4 }, minHeight: 84 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
              {isMobile ? (
                <IconButton
                  onClick={toggleMobileSidebar}
                  sx={{
                    border: '1px solid rgba(148, 163, 184, 0.24)',
                    backgroundColor: '#ffffff'
                  }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              ) : (
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    border: '1px solid rgba(148, 163, 184, 0.24)',
                    backgroundColor: '#ffffff'
                  }}
                >
                  <ArrowBackRoundedIcon />
                </IconButton>
              )}

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }} noWrap>
                  {currentMeta.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }} noWrap>
                  {currentMeta.subtitle}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  width: 42,
                  height: 42,
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                  fontWeight: 800
                }}
              >
                AH
              </Avatar>
              <Button
                onClick={handleLogout}
                startIcon={<LogoutRoundedIcon />}
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  borderRadius: 999,
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  color: '#0f172a',
                  fontWeight: 700,
                  backgroundColor: '#ffffff',
                  border: '1px solid rgba(148, 163, 184, 0.18)',
                  '&:hover': {
                    backgroundColor: '#f1f5f9'
                  }
                }}
              >
                Logout
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              display: currentMeta.surface === false ? 'block' : 'block',
              p: currentMeta.surface === false ? 0 : { xs: 2, md: 3 },
              borderRadius: currentMeta.surface === false ? 0 : 5,
              backgroundColor: currentMeta.surface === false ? 'transparent' : '#ffffff',
              border: currentMeta.surface === false ? 'none' : '1px solid rgba(148, 163, 184, 0.16)',
              boxShadow: currentMeta.surface === false ? 'none' : '0 20px 50px rgba(15, 23, 42, 0.08)'
            }}
          >
            <Routes>
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route path="/" element={<Dashboard />} />
              <Route path="/discovery/:tableName/:pageTitle/:viewType" element={<DynamicTablePageDiscovery />} />
              <Route path="/table/:tableName/:pageTitle/:viewType" element={<DynamicTablePage />} />
              <Route path="/edit-table/:tableName/:pageTitle/:viewType" element={<EditOnlyTablePage />} />
              <Route path="/manage-tour-type" element={<ManageTourType />} />
              <Route path="/manage-special-tour-type" element={<ManageSpecialTourType />} />
              <Route path="/manage-special-tour-type/add" element={<AddSpecialTourType />} />
              <Route path="/manage-special-tour-type/edit/:id" element={<EditSpecialTourType />} />
              <Route path="/manage-destination/:tourTypeId" element={<ManageDestination />} />
              <Route path="/manage-destination/add/:tourTypeId" element={<AddDestination />} />
              <Route path="/manage-destination/edit/:id" element={<EditDestination />} />
              <Route path="/manage-destination/itineraries/:destinations_id" element={<ManageItinerary />} />
              <Route path="/manage-itinerary/add/:destinations_id" element={<AddItinerary />} />
              <Route path="/manage-itinerary/edit/:id/:tourTypeId" element={<EditItinerary />} />
              <Route path="/manage-destination/gallery/:destinations_id" element={<ManageGallery />} />
              <Route path="/manage-gallery/add/:destinations_id" element={<AddGallery />} />
              <Route path="/manage-gallery/edit/:id" element={<EditGallery />} />
              <Route path="/manage-discovery" element={<ManageDiscovery />} />
              <Route path="/discovery/add" element={<AddEditDiscovery />} />
              <Route path="/discovery/edit/:id" element={<AddEditDiscovery />} />
              <Route path="/manage-partners" element={<ManagePartners />} />
              <Route path="/add-partner" element={<AddEditPartner />} />
              <Route path="/edit-partner/:id" element={<AddEditPartner />} />
              <Route path="/manage-locations" element={<ManageLocations />} />
              <Route path="/add-location" element={<AddEditLocation />} />
              <Route path="/edit-location/:id" element={<AddEditLocation />} />
              <Route path="/manage-core-competency" element={<ManageCoreCompetency />} />
              <Route path="/add-core-competency" element={<AddEditCoreCompetency />} />
              <Route path="/edit-core-competency/:id" element={<AddEditCoreCompetency />} />
              <Route path="/manage-accrediation" element={<ManageAccrediation />} />
              <Route path="/add-accrediation" element={<AddEditAccrediation />} />
              <Route path="/edit-accrediation/:id" element={<AddEditAccrediation />} />
              <Route path="/manage-continents" element={<ManageContinent />} />
              <Route path="/manage-continents/add" element={<AddEditContinent />} />
              <Route path="/manage-continents/edit/:id" element={<AddEditContinent />} />
              <Route path="/manage-tour-type/:continentId" element={<ManageTourType />} />
              <Route path="/manage-tour-type/add/:continentId" element={<AddEditTourType />} />
              <Route path="/manage-tour-type/edit/:id/:continentId" element={<AddEditTourType />} />
              <Route path="/manage-special-features/:tourTypeId" element={<ManageSpecialFeatures />} />
              <Route path="/add-special-feature/:tourTypeId" element={<AddEditSpecialFeature />} />
              <Route path="/edit-special-feature/:id/:tourTypeId" element={<AddEditSpecialFeature />} />
              <Route path="/testimonials" element={<TestimonialList />} />
              <Route path="/add-testimonial" element={<AddEditTestimonial />} />
              <Route path="/edit-testimonial/:id" element={<AddEditTestimonial />} />
              <Route path="/home-mantle" element={<HomeMantleList />} />
              <Route path="/add-home-mantle" element={<AddEditMantle />} />
              <Route path="/edit-home-mantle/:id" element={<AddEditMantle />} />
              <Route path="/about-mantle" element={<AboutMantleList />} />
              <Route path="/add-about-mantle" element={<AddEditAboutMantle />} />
              <Route path="/edit-about-mantle/:id" element={<AddEditAboutMantle />} />
              <Route path="/contact" element={<ContactList />} />
              <Route path="/user" element={<UserList />} />
              <Route path="/travel" element={<TravelList />} />
            </Routes>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

const AppWrapper = () => (
  <Router>
    <Routes>
      <Route path="*" element={<App />} />
    </Routes>
  </Router>
);

export default AppWrapper;
